import { usePageHeader } from "@hooks/use_page_header";
import { Button, Select } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkPage } from "@utils/router";
import { useQuery } from "@tanstack/react-query";
import { FinanceService } from "@services/finance.service";
import { useStudentBio } from "@hooks/use_student_bio";
import { VirtualAccountReport } from "@services/dtos/finance.dto";
import { LuChevronRight, LuInbox, LuReceipt } from "react-icons/lu";
import { Spiner } from "@components/loaders/spiner";
import { toRp } from "@utils/helper";
import moment from "moment-timezone";
import { FilterOutlined } from "@ant-design/icons";
import { PopupCustom } from "@components/general/popup";
moment.locale("id");

const statusConfig = (status: string, vaRejectMessage?: string | null) => {
  if (status === "COMPLETED")
    return {
      label: "Berhasil",
      bg: "#f0fdf4",
      color: "#16a34a",
      border: "#bbf7d0",
    };
  if (status === "EXPIRED")
    return {
      label: "Dibatalkan",
      bg: "#fef2f2",
      color: "#dc2626",
      border: "#fecaca",
    };
  if (status === "ONHOLD")
    return vaRejectMessage
      ? {
          label: "Bukti Ditolak",
          bg: "#fff7ed",
          color: "#ea580c",
          border: "#fed7aa",
        }
      : {
          label: "Menunggu Konfirmasi",
          bg: "#f0fdf4",
          color: "#166534",
          border: "#bbf7d0",
        };
  if (status === "REJECTED")
    return {
      label: "Bukti Ditolak",
      bg: "#fff7ed",
      color: "#ea580c",
      border: "#fed7aa",
    };
  return {
    label: "Menunggu Pembayaran",
    bg: "#fffbeb",
    color: "#d97706",
    border: "#fde68a",
  };
};

export const VaDataPage = () => {
  const navigate = useNavigate();
  usePageHeader("Laporan VA", { bottomNavigation: "home" });
  const [limit, setLimit] = useState<string>("10");
  const [popupFilter, setPopupFilter] = useState<boolean>(false);

  const { nis } = useStudentBio();

  const { data, isPending } = useQuery({
    queryKey: [FinanceService.queries.GET_FINANCE_BILLS_PAID_OF, nis, limit],
    queryFn: () => FinanceService.virtualAccountReport({ nis, limit }),
  });

  const onOpenDetail = (dataPayment: VirtualAccountReport) => {
    navigate(linkPage.VA_DETAIL_PAGE, { state: dataPayment });
  };

  return (
    <>
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        {/* filter bar */}
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <div className="tw-text-sm tw-text-text-secondary">
            Menampilkan{" "}
            <span className="tw-font-semibold tw-text-text-primary">
              {limit} data
            </span>
          </div>
          <FilterOutlined
            className="tw-p-2 tw-text-xl tw-bg-transparent card-body"
            onClick={() => setPopupFilter(true)}
          />
        </div>

        {isPending ? (
          <div className="tw-text-center card-body tw-py-8">
            <Spiner />
          </div>
        ) : data?.data != null && data.data.length > 0 ? (
          <div className="tw-space-y-3">
            {data.data.map((item, idx) => {
              const s = statusConfig(item.vaStatus, item.vaRejectMessage);
              const isCompleted = item.vaStatus === "COMPLETED";
              return (
                <div
                  key={idx}
                  className="card-body tw-flex tw-items-center tw-gap-3 tw-cursor-pointer"
                  onClick={() => onOpenDetail(item)}
                >
                  <div
                    className="tw-flex tw-items-center tw-justify-center tw-rounded-2xl tw-w-10 tw-h-10 tw-shrink-0"
                    style={{ background: "#f0fdf4" }}
                  >
                    <LuReceipt className="tw-text-lg tw-text-primary" />
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-text-sm tw-font-semibold tw-text-text-primary tw-truncate">
                      {item.vaTrxId}
                    </div>
                    <div className="tw-text-xs tw-text-text-tertiary tw-mt-0.5">
                      {isCompleted ? "Dibayar: " : "Exp: "}
                      {moment(
                        isCompleted ? item.vaDatePayment : item.vaDateExpired,
                      ).format("DD MMM YYYY · HH:mm")}
                    </div>
                  </div>
                  <div className="tw-flex tw-flex-col tw-items-end tw-gap-1 tw-shrink-0">
                    <div className="tw-text-sm tw-font-bold tw-text-text-primary">
                      {toRp(item.vaTrxAmount ?? 0)}
                    </div>
                    <span
                      className="tw-text-[10px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full"
                      style={{
                        background: s.bg,
                        color: s.color,
                        border: `1px solid ${s.border}`,
                      }}
                    >
                      {s.label}
                    </span>
                  </div>
                  <LuChevronRight className="tw-text-background-400 tw-shrink-0" />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="tw-p-8 tw-text-center card-body">
            <LuInbox className="tw-mx-auto tw-mb-2 tw-text-5xl tw-text-text-secondary tw-opacity-40" />
            <div className="tw-text-sm tw-text-text-secondary">
              Data tidak ditemukan
            </div>
          </div>
        )}
      </div>

      <PopupCustom
        title="Filter Data"
        open={popupFilter}
        onCancel={() => setPopupFilter(false)}
        slideUpModal={true}
      >
        <Select
          className="tw-text-base"
          placeholder="Pilih Jumlah Ditampilkan"
          defaultValue={limit}
          style={{ width: "100%" }}
          allowClear
          onChange={(e) => setLimit(e)}
          options={[
            { value: "5", label: "5 data terakhir" },
            { value: "10", label: "10 data terakhir" },
            { value: "25", label: "25 data terakhir" },
          ]}
        />
        <Button
          onClick={() => setPopupFilter(false)}
          className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-sm tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
          style={{ background: "#ef4444" }}
        >
          Tutup
        </Button>
      </PopupCustom>
    </>
  );
};
